import React, { useEffect } from 'react';
import { Link, useParams, useNavigate } from 'react-router-dom';
import {
    ArrowRight, CheckCircle, Warning, ShieldCheck, ListChecks, House,
    Camera, ShareFat, Stamp,
} from '@phosphor-icons/react';
import { findHousebuilder, HOUSEBUILDERS } from '../data/housebuilders';

/**
 * /snag/<slug> — SEO landing pages for UK housebuilders.
 *
 * One template, data-driven from /data/housebuilders.js. To add a new
 * builder, just append to that file — no changes needed here.
 *
 * SEO essentials handled inline (avoid pulling in react-helmet just for
 * a handful of pages):
 *   - document.title set per builder
 *   - canonical link rel
 *   - meta description per builder
 *   - JSON-LD WebPage + Organization schema
 *
 * Legal: every page renders a trademark / non-affiliation disclaimer
 * in the footer. Builder names are factual references only; we never
 * use their logos and never imply association.
 */
export default function BuilderSnagging() {
    const { slug } = useParams();
    const navigate = useNavigate();
    const builder = findHousebuilder(slug);

    useEffect(() => {
        if (!builder) {
            // Unknown slug — bounce to the landing page rather than a 404
            // so users (and bots) always reach something useful.
            navigate('/', { replace: true });
            return;
        }
        const title = `Snag your ${builder.name} | DefectR — defects rectified`;
        const desc = `Document every defect in your ${builder.name} new build. Photo evidence, floorplan pins, shareable report with your builder — built for UK homeowners. Free to start.`;
        document.title = title;

        const setMeta = (name, content) => {
            let el = document.querySelector(`meta[name="${name}"]`);
            if (!el) {
                el = document.createElement('meta');
                el.setAttribute('name', name);
                document.head.appendChild(el);
            }
            el.setAttribute('content', content);
        };
        const setLink = (rel, href) => {
            let el = document.querySelector(`link[rel="${rel}"]`);
            if (!el) {
                el = document.createElement('link');
                el.setAttribute('rel', rel);
                document.head.appendChild(el);
            }
            el.setAttribute('href', href);
        };

        setMeta('description', desc);
        setMeta('keywords', `${builder.name.toLowerCase()} snag list, ${builder.name.toLowerCase()} defects, new build snagging, NHBC, ${builder.name.toLowerCase()} aftercare`);
        setLink('canonical', `https://defectr.co.uk/snag/${builder.slug}`);

        // JSON-LD — single inline script so Google can grok the page.
        // Removed on unmount so we don't leak schema between pages.
        const ld = document.createElement('script');
        ld.type = 'application/ld+json';
        ld.textContent = JSON.stringify({
            '@context': 'https://schema.org',
            '@type': 'WebPage',
            name: title,
            description: desc,
            url: `https://defectr.co.uk/snag/${builder.slug}`,
            mainEntity: {
                '@type': 'Service',
                name: `Snagging tool for ${builder.name} homes`,
                provider: {
                    '@type': 'Organization',
                    name: 'DefectR Ltd',
                    url: 'https://defectr.co.uk',
                },
                areaServed: 'United Kingdom',
                offers: {
                    '@type': 'Offer',
                    price: '0.00',
                    priceCurrency: 'GBP',
                    description: 'Free Foundation tier — paid upgrades from £14.99.',
                },
            },
        });
        document.head.appendChild(ld);
        return () => {
            try { document.head.removeChild(ld); } catch { /* no-op */ }
        };
    }, [builder, navigate]);

    if (!builder) return null;

    return (
        <div className="min-h-screen bg-white text-[#09090B]" data-testid={`builder-page-${builder.slug}`}>
            {/* Header */}
            <header className="border-b border-[#18181B] sticky top-0 bg-white z-30">
                <div className="max-w-5xl mx-auto px-4 sm:px-6 py-3 flex items-center justify-between">
                    <Link to="/" data-testid="builder-page-home-link" className="inline-flex items-center">
                        <img src="/defectr-logo.png" alt="DefectR" className="h-8 w-auto object-contain" />
                    </Link>
                    <Link
                        to="/"
                        data-testid="builder-page-cta-top"
                        className="font-mono text-xs uppercase tracking-[0.2em] px-4 py-2 border border-[#18181B] hover:bg-[#09090B] hover:text-white transition-all duration-150 active:scale-95"
                    >
                        Start free
                    </Link>
                </div>
            </header>

            <main className="max-w-3xl mx-auto px-4 sm:px-6 py-10">
                {/* Hero */}
                <section className="mb-12">
                    <div className="label-tiny mb-3">
                        For {builder.name} homeowners · UK
                    </div>
                    <h1 className="font-display text-4xl sm:text-5xl font-bold tracking-tight leading-tight">
                        <span className="block">{builder.tagline.split('—')[0].trim()}</span>
                        {builder.tagline.includes('—') && (
                            <span className="inline-block bg-[#1FA5CF] text-white px-3 py-0.5 mt-2 text-2xl sm:text-3xl">
                                {builder.tagline.split('—')[1].trim()}
                            </span>
                        )}
                    </h1>
                    <p className="mt-6 text-base text-[#52525B] leading-relaxed">
                        {builder.intro}
                    </p>
                    {builder.hbfRating && (
                        <div className="mt-4 inline-flex items-center gap-2 text-xs font-mono uppercase tracking-[0.18em] text-[#52525B] border border-[#E4E4E7] px-3 py-1.5">
                            <Stamp size={12} weight="duotone" />
                            HBF: {builder.hbfRating}
                        </div>
                    )}
                </section>

                {/* CTA banner */}
                <section className="bg-[#E8F5FB] border-2 border-[#1FA5CF] p-5 mb-12">
                    <div className="flex items-start gap-3">
                        <Camera size={28} weight="duotone" color="#1787AB" className="mt-1 flex-none" />
                        <div className="flex-1">
                            <h2 className="font-display text-xl font-semibold">Start your {builder.name} snag list — free</h2>
                            <p className="mt-1 text-sm text-[#52525B]">
                                Take photos, drop pins on your floorplan, share with your builder. Up to 10 defects free on the Foundation tier.
                            </p>
                        </div>
                        <Link
                            to="/"
                            data-testid="builder-page-cta-main"
                            className="inline-flex items-center gap-1 bg-[#1FA5CF] text-white px-4 py-2.5 font-mono text-xs uppercase tracking-[0.2em] hover:bg-[#1787AB] active:scale-95 transition-all duration-150 self-center flex-none"
                        >
                            Start
                            <ArrowRight size={14} weight="bold" />
                        </Link>
                    </div>
                </section>

                {/* Common defects */}
                {builder.commonDefects?.length > 0 && (
                    <section className="mb-12">
                        <div className="flex items-center gap-2 mb-4">
                            <Warning size={20} weight="duotone" color="#F59E0B" />
                            <h2 className="font-display text-2xl font-semibold">Common {builder.name} defects to check</h2>
                        </div>
                        <p className="text-sm text-[#52525B] mb-4">
                            Based on publicly available HBF Customer Satisfaction Survey data and the typical pattern of complaints in UK new-build aftercare. Use this as your starter checklist when you walk the property.
                        </p>
                        <ul className="space-y-2.5">
                            {builder.commonDefects.map((d, i) => (
                                <li key={i} className="flex items-start gap-2 text-sm">
                                    <CheckCircle size={16} weight="fill" color="#1FA5CF" className="mt-0.5 flex-none" />
                                    <span>{d}</span>
                                </li>
                            ))}
                        </ul>
                    </section>
                )}

                {/* NHBC */}
                {builder.nhbcWarrantyNote && (
                    <section className="mb-12 border-l-4 border-[#1FA5CF] pl-5">
                        <div className="flex items-center gap-2 mb-2">
                            <ShieldCheck size={20} weight="duotone" color="#1FA5CF" />
                            <h2 className="font-display text-xl font-semibold">Your warranty window</h2>
                        </div>
                        <p className="text-sm text-[#52525B]">{builder.nhbcWarrantyNote}</p>
                    </section>
                )}

                {/* How DefectR helps */}
                <section className="mb-12">
                    <div className="flex items-center gap-2 mb-5">
                        <ListChecks size={20} weight="duotone" color="#1FA5CF" />
                        <h2 className="font-display text-2xl font-semibold">How DefectR helps</h2>
                    </div>
                    <div className="grid sm:grid-cols-3 gap-4">
                        <HelpCard
                            icon={<Camera size={20} weight="duotone" color="#1FA5CF" />}
                            title="Photo evidence"
                            body="Every defect comes with a date-stamped photo, vector arrows pointing at the problem, and severity rating."
                        />
                        <HelpCard
                            icon={<House size={20} weight="duotone" color="#1FA5CF" />}
                            title="Floorplan pins"
                            body={`Drop a pin exactly where the issue is on your ${builder.name} floorplan — no guessing where each defect lives.`}
                        />
                        <HelpCard
                            icon={<ShareFat size={20} weight="duotone" color="#1FA5CF" />}
                            title="One-tap share"
                            body="Send the whole report to your builder via shareable link or branded PDF — no more email chains."
                        />
                    </div>
                </section>

                {/* Bottom CTA */}
                <section className="bg-[#09090B] text-white p-8 mb-12">
                    <h2 className="font-display text-2xl font-semibold">Start your {builder.name} snag list today</h2>
                    <p className="mt-2 text-sm text-[#A1A1AA]">
                        Free to start. £14.99 per property if you want unlimited defects and the builder share link. No subscription.
                    </p>
                    <Link
                        to="/"
                        data-testid="builder-page-cta-bottom"
                        className="mt-6 inline-flex items-center gap-2 bg-[#1FA5CF] text-white px-6 py-3.5 font-mono text-sm uppercase tracking-[0.2em] hover:bg-[#1787AB] active:scale-95 transition-all duration-150"
                    >
                        Start free <ArrowRight size={16} weight="bold" />
                    </Link>
                </section>

                {/* Other builders */}
                <section className="mb-12">
                    <h3 className="font-mono text-[10px] uppercase tracking-[0.2em] text-[#52525B] mb-3">
                        Browse other UK housebuilders
                    </h3>
                    <div className="flex flex-wrap gap-2">
                        {HOUSEBUILDERS.filter(b => b.slug !== builder.slug).map(b => (
                            <Link
                                key={b.slug}
                                to={`/snag/${b.slug}`}
                                data-testid={`builder-page-other-${b.slug}`}
                                className="px-3 py-1.5 text-xs border border-[#E4E4E7] hover:border-[#1FA5CF] hover:text-[#1787AB] transition-colors duration-150"
                            >
                                {b.name}
                            </Link>
                        ))}
                    </div>
                </section>
            </main>

            {/* Footer with disclaimer */}
            <footer className="border-t border-[#18181B]">
                <div className="max-w-5xl mx-auto px-4 sm:px-6 py-6 space-y-3">
                    <p className="text-[11px] text-[#52525B] leading-relaxed">
                        <strong className="text-[#09090B]">Not affiliated with {builder.legalName || builder.name}.</strong>{' '}
                        DefectR is an independent UK consumer tool. {builder.name}, its name and any related marks are trademarks of their respective owners. DefectR is operated by <strong>DefectR Ltd</strong> (England &amp; Wales, Co. No. 09929002) and is not endorsed by, or affiliated with, {builder.legalName || builder.name}. All references to common defects are informational and drawn from publicly available sources including the Home Builders Federation Customer Satisfaction Survey.
                    </p>
                    <div className="flex flex-wrap items-center justify-between gap-3 text-xs font-mono uppercase tracking-[0.2em] text-[#52525B] pt-3 border-t border-[#E4E4E7]">
                        <span>© DefectR Ltd</span>
                        <div className="flex items-center gap-4">
                            <Link to="/privacy" className="underline hover:text-[#1787AB]">Privacy</Link>
                            <Link to="/terms" className="underline hover:text-[#1787AB]">Terms &amp; refunds</Link>
                            <Link to="/" className="underline hover:text-[#1787AB]">DefectR home</Link>
                        </div>
                    </div>
                </div>
            </footer>
        </div>
    );
}

function HelpCard({ icon, title, body }) {
    return (
        <div className="border border-[#E4E4E7] p-4">
            <div>{icon}</div>
            <h3 className="font-display font-semibold mt-2 text-sm">{title}</h3>
            <p className="mt-1 text-xs text-[#52525B] leading-relaxed">{body}</p>
        </div>
    );
}
